/**
 * P4 (Mimmo, 21.10.2026): „Ich habe Julia den Link über die Restzahlung gesendet, aber ich weiß
 * nicht, ob es geklappt hat. Sie wollte eine Rechnung über die Restzahlung, aber das kann ich nicht
 * erstellen." Zwei reine Anzeigen, keine Zahlungslogik:
 *
 *  - `ZahlungslinkVersand`: je offener Zahlung eine Zeile in Worten — wann und an wen der Link
 *    ging und ob die Mail ankam (Felder `linkGesendetAm/An/Zustellung` aus GET /api/events/:id/payments).
 *  - `RestzahlungHinweis`: warum die Restzahlungsrechnung nicht entstehen kann und wo es weitergeht,
 *    statt nur einer roten Fehlerzeile am Knopf.
 */
import type { Payment } from "../types";
import { PAYMENT_TYPE_LABEL } from "../format";
import { Button, Icon } from "../components";

const OFFEN = new Set(["pending", "processing"]);

const TAG_UHR = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

/** „19.10., 12:40" aus einem ISO-Zeitpunkt. */
export function versandZeitpunkt(iso: string): string {
  const teile = Object.fromEntries(TAG_UHR.formatToParts(new Date(iso)).map((t) => [t.type, t.value]));
  return `${teile.day}.${teile.month}., ${teile.hour}:${teile.minute}`;
}

export type VersandZeile = { text: string; ton: "neutral" | "gut" | "fehler" };

export function versandZeile(p: Pick<Payment, "linkGesendetAm" | "linkGesendetAn" | "linkZustellung">): VersandZeile {
  if (!p.linkGesendetAm) return { text: "Noch kein Link gesendet", ton: "neutral" };
  const wann = `Link gesendet am ${versandZeitpunkt(p.linkGesendetAm)}${p.linkGesendetAn ? ` an ${p.linkGesendetAn}` : ""}`;
  if (p.linkZustellung === "zugestellt") return { text: `${wann} · Zugestellt`, ton: "gut" };
  if (p.linkZustellung === "fehlgeschlagen") return { text: `${wann} · Nicht zugestellt`, ton: "fehler" };
  return { text: `${wann} · Zustellung noch nicht bestätigt`, ton: "neutral" };
}

export function ZahlungslinkVersand({ payments }: { payments: Payment[] }) {
  const offene = payments.filter((p) => OFFEN.has(p.status));
  if (offene.length === 0) return null;
  return (
    <ul className="space-y-1" aria-label="Versand der Zahlungslinks" data-testid="zahlungslink-versand">
      {offene.map((p) => {
        const z = versandZeile(p);
        return (
          <li key={p.id} className="flex items-start gap-1.5 font-body-sm text-[11.5px]" data-testid={`zahlungslink-versand-${p.id}`}>
            <Icon name={z.ton === "fehler" ? "error" : z.ton === "gut" ? "mark_email_read" : "mail"} className={`mt-px shrink-0 text-[14px] ${z.ton === "fehler" ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"}`} />
            <span className={`min-w-0 break-words ${z.ton === "fehler" ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"}`}>
              <span className="font-semibold">{PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}:</span> {z.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export type RestzahlungHinweisDaten = { code: string; message: string };

/** Codes, für die es einen ruhigen Hinweis mit Weg statt einer Fehlerzeile gibt. */
export const RESTZAHLUNG_HINWEIS_CODES = ["abrechnung_vorhanden", "address_incomplete", "land_fehlt", "no_invoicer", "kein_mailer", "betrag_weicht_ab"] as const;

export function istRestzahlungHinweisCode(code: string | undefined): code is (typeof RESTZAHLUNG_HINWEIS_CODES)[number] {
  return Boolean(code) && (RESTZAHLUNG_HINWEIS_CODES as readonly string[]).includes(code as string);
}

export function RestzahlungHinweis({
  hinweis,
  onZurAbrechnung,
  onZumKunden,
}: {
  hinweis: RestzahlungHinweisDaten;
  onZurAbrechnung: () => void;
  onZumKunden?: () => void;
}) {
  let text: string;
  let knopf: { label: string; icon: string; onClick: (() => void) | undefined } | null = null;
  switch (hinweis.code) {
    case "abrechnung_vorhanden":
      text = "Die Abrechnung ist schon angelegt — die Rechnung über die Restzahlung entsteht dort als Schlussrechnung.";
      knopf = { label: "Zur Abrechnung", icon: "receipt_long", onClick: onZurAbrechnung };
      break;
    case "address_incomplete":
      text = "Rechnungsadresse unvollständig — bitte beim Kunden ergänzen.";
      knopf = { label: "Zum Kunden", icon: "person", onClick: onZumKunden };
      break;
    case "land_fehlt":
      text = "Rechnungsadresse unvollständig — Land fehlt. Bitte beim Kunden ergänzen.";
      knopf = { label: "Zum Kunden", icon: "person", onClick: onZumKunden };
      break;
    case "no_invoicer":
      text = "Rechnungen gibt es ab der Anbindung von LexOffice (Einstellungen → Module).";
      break;
    case "kein_mailer":
      text = "Der Mailversand ist nicht eingerichtet — die Rechnung kann so noch nicht an den Gast gehen.";
      break;
    case "betrag_weicht_ab":
      text = "Der Betrag der Restzahlung passt nicht zum Angebot — bitte den Zahlungsplan in der Abrechnung prüfen.";
      knopf = { label: "Zur Abrechnung", icon: "receipt_long", onClick: onZurAbrechnung };
      break;
    default:
      text = hinweis.message;
  }
  return (
    <div role="status" data-testid="restzahlung-hinweis" className="flex flex-wrap items-center gap-2 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
      <Icon name="info" className="mt-px shrink-0 text-[14px]" />
      <span className="min-w-0 flex-1 break-words">{text}</span>
      {knopf && knopf.onClick && (
        <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" onClick={knopf.onClick}>
          <Icon name={knopf.icon} className="text-[15px]" /> {knopf.label}
        </Button>
      )}
    </div>
  );
}
